import { useMemo } from 'react';
import { Modal } from '../modal/modal';
import { ImportExportAction, ImportExportAdapter } from './types';
import { FileField } from '../field/field';
import { Spinner } from '../spinner/spinner';
import { Button } from '../form/button';
import { Typography } from '../typography/typography';
import {
  DEFAULT_FILE_TYPE,
  ImportExportCallbacks,
  useImportExportModalState,
} from './hooks/use-import-export-modal-state';

export const getTitleFromTypeAndAction = (type: string, action: ImportExportAction): string => {
  const actionLabel = action === 'import' ? 'Import' : 'Export';
  return `${actionLabel} ${type}`;
};

type ImportExportProps<TI, TE> = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  action: ImportExportAction;
  adapter: ImportExportAdapter<TI, TE>;
  initialImportParams: TI;
  initialExportParams: TE;
  callbacks?: ImportExportCallbacks;
};

export const ImportExportModal = <TI, TE>({
  isOpen,
  setIsOpen,
  action,
  adapter,
  initialImportParams,
  initialExportParams,
  callbacks,
}: ImportExportProps<TI, TE>) => {
  const {
    isSaving,
    file,
    setFile,
    importParams,
    setImportParams,
    exportParams,
    setExportParams,
    close,
    onImport,
    exportSampleFile,
  } = useImportExportModalState({
    isOpen,
    setIsOpen,
    initialImportParams,
    initialExportParams,
    adapter,
    action,
    callbacks,
  });

  const isImport = action === 'import';
  const isExport = action === 'export';

  const ModalActions = useMemo(
    () =>
      isExport ? (
        <>
          <Button className="button button-small button-block button-secondary" disabled={isSaving} onClick={close}>
            <Typography type="text-sm" weight="bold" className="text-[var(--quipli-black-800)]">
              Cancel export
            </Typography>
          </Button>
        </>
      ) : (
        <>
          <Button className="button button-small button-block button-secondary" disabled={isSaving} onClick={close}>
            <Typography type="text-sm" weight="bold" className="text-[var(--quipli-black-800)]">
              Cancel
            </Typography>
          </Button>
          <Button
            className="button button-yellow button-small button-block"
            disabled={isSaving || !file}
            onClick={onImport}
          >
            <Typography data-cy="import-button" type="text-sm" weight="bold" className="text-[var(--quipli-black-800)]">
              Import {adapter.typeLabel}
            </Typography>
          </Button>
        </>
      ),
    [isExport, isSaving, close, onImport, adapter.typeLabel, file],
  );

  return (
    <Modal
      isOpen={isOpen}
      close={close}
      title={getTitleFromTypeAndAction(adapter.typeLabel, action)}
      actions={ModalActions}
    >
      <div>
        {isImport && adapter.renderSampleFile?.({ exportSampleFile })}
        {isImport && adapter.renderImportFields?.({ params: importParams, setParams: setImportParams })}
        {isExport && adapter.renderExportFields?.({ params: exportParams, setParams: setExportParams })}

        {isExport && isSaving && (
          <div data-cy="export-spinner" className="flex flex-col justify-center items-center gap-2 m-4">
            <Spinner />
            <Typography type="text-sm" className="text-quipli-black-800">
              Loading...
            </Typography>
          </div>
        )}

        {isImport && (
          <div className="mt-4">
            <FileField
              className="w-full h-full resize-none text-sm"
              label="File"
              value={file}
              accept={adapter.fileAccept ?? DEFAULT_FILE_TYPE}
              onChange={(newFile) => setFile(newFile as File)}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};
